'use client';

import { type ComponentProps, memo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Box, Combobox } from '@zillow/constellation-10';

// Constellation 10.14's `renderInputGroup` yields `groupProps` typed as
// `Partial<ComboboxInputGroupPropsInterface>`, but JSX-type inference for
// `<Combobox.InputGroup>` narrows to plain `RefAttributes` when a spread is
// involved. 10.17 fixes the inference. Cast to the component's own
// `ComponentProps` so the spread satisfies the narrow inferred type without
// `@ts-expect-error`.
type ComboboxInputGroupProps = ComponentProps<typeof Combobox.InputGroup>;
type ComboboxInputProps = ComponentProps<typeof Combobox.Input>;
import { MapPin } from 'lucide-react';
import { token } from '@/styled-system/tokens';
import { LucideIcon } from '@/src/components/LucideIcon';
import type { AreaResponse, AutocompleteResult } from '@/src/lib/api';
import { keepAllOptions, useLocationSuggestions, type LocationAnalyticsSurface } from './useLocationSuggestions';

// Token the ListingSearch wrapper and the active Rooms/Price triggers both
// paint, so the Location trigger reads as flush with the wrapper instead of
// literally transparent. See the `css` prop below for the full rationale.
const TRIGGER_BG_TOKEN = 'bg.action.neutral.alt.default';

type LocationComboboxProps = {
  /** Currently selected area (live mode) or seeded area (navigate mode). */
  area: AreaResponse | AutocompleteResult | null;
  /** IP-derived city for the "Near Me" affordance. */
  nearMeArea?: AreaResponse | null;
  /** Called when the user picks a suggestion or "Near Me". */
  onSelect: (result: AutocompleteResult) => void;
  /**
   * Called when Enter should run the search for a suggestion right away:
   * either the suggestion Constellation just selected on this keystroke, or
   * the top-ranked suggestion when nothing is highlighted. Fires after
   * `onSelect` for the same result.
   */
  onEnterSearch?: (result: AutocompleteResult) => void;
  /** Called on every keystroke so the parent can cancel recent-search seeding. */
  onUserInput?: () => void;
  size?: 'sm' | 'md' | 'lg';
  analyticsSurface?: LocationAnalyticsSurface;
  compact?: boolean;
  placeholder?: string;
  testIdSuffix?: string;
};

/**
 * Internal Combobox wrapper for ListingSearch only. Wraps Constellation's
 * Combobox primitive with `useLocationSuggestions` — HotPads' debounced area
 * autocomplete API, recent areas, and the IP-derived Near Me affordance.
 */
function LocationCombobox({
  area,
  nearMeArea,
  onSelect,
  onEnterSearch,
  onUserInput,
  size = 'md',
  analyticsSurface = size === 'lg' ? 'homepage' : 'search',
  compact = false,
  placeholder,
  testIdSuffix,
}: LocationComboboxProps) {
  const tSearch = useTranslations('search');

  const {
    idPrefix,
    inputValue,
    loading,
    options,
    handleInputValueChange,
    handleOptionSelect,
    handleEnterCapture,
    handleEnterKeyDown,
  } = useLocationSuggestions({
    area,
    nearMeArea,
    onSelect,
    onEnterSearch,
    onUserInput,
    analyticsSurface,
  });

  const setClearButtonTestId = useCallback(
    (element: HTMLSpanElement | null) => {
      const testId = testIdSuffix ? `button-clear-field-selection-${testIdSuffix}` : 'button-clear-field-selection';
      element?.querySelector('button')?.setAttribute('data-testid', testId);
    },
    [testIdSuffix],
  );

  return (
    // Keyboard interception wrapper only — focus and interactivity live on
    // the Combobox input inside.
    <Box
      asChild
      css={{
        // C11n 10.18 hardcodes clear-control spacing; compact SRP needs the Figma inset.
        '&[data-compact="true"] [data-c11n-component="Combobox.Clear"]': {
          marginInlineEnd: '-tighter',
          '& [data-c11n-component="IconButton"]': {
            marginInline: 'none',
          },
        },
      }}
    >
      <div
        data-compact={compact ? 'true' : undefined}
        onKeyDownCapture={handleEnterCapture}
        onKeyDown={handleEnterKeyDown}
      >
        <Combobox
          data-c11n-component="listing-search-location"
          data-testid={idPrefix}
          appearance="input"
          freeForm
          showLabelForValue
          selectCallbackFrequency="always"
          fluidDropdown
          size={size}
          placeholder={placeholder ?? tSearch('findApartmentsIn')}
          aria-label={tSearch('searchByLocation')}
          loading={loading}
          value={inputValue}
          onChange={(next) => {
            if (typeof next === 'string') handleInputValueChange(next);
          }}
          onOptionSelect={handleOptionSelect}
          options={options}
          optionFilter={keepAllOptions}
          // Forwarded to Combobox.Trigger — fills the flex-item parent in
          // ListingSearch so the location field grows to take the available width.
          // The data-c11n-component selector resets text-align on the underlying
          // <input> so the homepage hero's centered alignment doesn't cascade in.
          // NOTE: this can't be hoisted into one object with a conditional
          // spread for the Combobox.Input font-size override below -- Panda's
          // static extraction can't see through `...(size !== 'lg' && {...})`
          // and silently emits broken CSS (confirmed via `panda cssgen`). Two
          // fully static branches is the pattern the codebase already documents
          // as reliably extractable (see the container-query caveat in
          // docs/context/styling.md).
          css={
            size === 'lg'
              ? {
                  width: '100%',
                  borderRadius: 'pill',
                  boxShadow: 'none',
                  // The active Rooms/Price triggers (Button emphasis="outlined"
                  // tone="brand") paint `bg.action.neutral.alt.default`, the same
                  // token the ListingSearch wrapper itself uses -- so they read as
                  // flush with the wrapper rather than literally transparent. Match
                  // that token directly (not the `transparent` keyword) so this
                  // stays correct if the wrapper's token ever changes. This `css`
                  // prop lands on `Combobox.Trigger` itself, which paints
                  // `Combobox.Trigger.bg.default` (-> bg.neutral, blue.700 in dark
                  // mode) and repaints on hover/active/focus, so all of those states
                  // need overriding, not just the base. `Combobox.InputGroup` (a
                  // nested child) never had a background of its own.
                  backgroundColor: TRIGGER_BG_TOKEN,
                  _hover: { backgroundColor: TRIGGER_BG_TOKEN },
                  _active: { backgroundColor: TRIGGER_BG_TOKEN },
                  '&:has(input:focus)': { backgroundColor: TRIGGER_BG_TOKEN },
                  '& [data-c11n-component="Combobox.InputGroup"]': {
                    borderRadius: 'pill',
                  },
                  '& [data-c11n-component="Combobox.Input"]': {
                    textAlign: 'start',
                    fontWeight: 'bold',
                  },
                }
              : {
                  width: '100%',
                  paddingBlock: compact ? 'none' : undefined,
                  // Owns its own leading inset: the bar container is padded to
                  // `tightest`, so this is what holds the pin clear of the pill's curve.
                  paddingInlineStart: 'default',
                  paddingInlineEnd: 'none',
                  borderRadius: 'pill',
                  boxShadow: 'none',
                  backgroundColor: TRIGGER_BG_TOKEN,
                  _hover: { backgroundColor: TRIGGER_BG_TOKEN },
                  _active: { backgroundColor: TRIGGER_BG_TOKEN },
                  '&:has(input:focus)': { backgroundColor: TRIGGER_BG_TOKEN },
                  '& [data-c11n-component="Combobox.InputGroup"]': {
                    borderRadius: 'pill',
                  },
                  '& [data-c11n-component="Combobox.Input"]': {
                    textAlign: 'start',
                    fontWeight: 'bold',
                  },
                }
          }
          renderInput={(inputProps) => (
            // AutocompleteV2Input mirrors legacy hotpads-web's testid on the same element, so the
            // shared Sonar monitors can assert one selector against both apps.
            <Combobox.Input
              {...(inputProps as ComboboxInputProps)}
              style={
                size === 'sm'
                  ? {
                      ...inputProps.style,
                      fontSize: token.var('fontSizes.body'),
                      fontWeight: token.var('fontWeights.regular'),
                      lineHeight: token.var('lineHeights.body'),
                      letterSpacing: token.var('letterSpacings.body'),
                    }
                  : inputProps.style
              }
              id={idPrefix}
              // HP-9041: overrides Constellation's autocomplete="new-password", which lets Chrome
              // paint its own popup over our suggestions. Combobox.Input spreads caller props
              // after its own default, so this wins wherever it sits in this JSX (`inputProps`
              // carries only aria-* attributes). The field also drops `name`, which nothing read
              // and which gave Chrome's history a stable key; `id` still clears the DevTools
              // id-or-name requirement that put it there.
              autoComplete="off"
              data-testid="AutocompleteV2Input"
            />
          )}
          renderClear={(clearProps) => <Combobox.Clear {...clearProps} ref={setClearButtonTestId} />}
          renderInputGroup={(groupProps) => (
            <Combobox.InputGroup {...(groupProps as ComboboxInputGroupProps)}>
              <LucideIcon icon={MapPin} size={size === 'sm' ? 'sm' : 'md'} />
              {groupProps.children}
            </Combobox.InputGroup>
          )}
          renderOptionMedia={() => (
            <Combobox.OptionMedia>
              <LucideIcon icon={MapPin} size="sm" />
            </Combobox.OptionMedia>
          )}
        />
      </div>
    </Box>
  );
}

// Memoized so the search bar's optimistic-patch commits (which change no prop
// here) skip reconciling this subtree on every filter click.
export default memo(LocationCombobox);
